import type { ActivityScope } from "@solution-advisor/context-events";
import {
	createContext,
	type FC,
	type MutableRefObject,
	type PropsWithChildren,
	useContext,
	useEffect,
	useRef,
	useState
} from "react";

import { type ActivityTracker, createActivityTracker } from "src/activity/activityTracker";
import { browserTransport } from "src/activity/browserTransport";

interface ActivityContextValue {
	tracker: ActivityTracker;
	scope: MutableRefObject<ActivityScope | undefined>;
}

const ActivityContext = createContext<ActivityContextValue | null>(null);

/** One tab = one activity session. A reload keeps it; a new tab starts another. */
const SESSION_STORAGE_KEY = "sa-activity-session";

const tabSessionId = (): string => {
	try {
		const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
		if (existing) return existing;
		const created = crypto.randomUUID();
		sessionStorage.setItem(SESSION_STORAGE_KEY, created);
		return created;
	} catch {
		return crypto.randomUUID();
	}
};

const currentPage = () => ({ path: window.location.pathname, title: document.title || undefined });

/** Captures activity for signed-in users only (the API refuses it otherwise). */
export const ActivityProvider: FC<PropsWithChildren> = ({ children }) => {
	const scope = useRef<ActivityScope | undefined>(undefined);
	const [tracker] = useState(() =>
		createActivityTracker({
			transport: browserTransport,
			sessionId: tabSessionId(),
			getPage: currentPage,
			getScope: () => scope.current
		})
	);

	useEffect(() => {
		const onHidden = () => {
			if (document.visibilityState === "hidden") tracker.flushOnExit();
		};
		const onPageHide = () => tracker.flushOnExit();
		document.addEventListener("visibilitychange", onHidden);
		window.addEventListener("pagehide", onPageHide);
		return () => {
			document.removeEventListener("visibilitychange", onHidden);
			window.removeEventListener("pagehide", onPageHide);
			// Nothing may stay queued (or on a timer) past the shell that tracked it.
			tracker.flushOnExit();
		};
	}, [tracker]);

	return <ActivityContext value={{ tracker, scope }}>{children}</ActivityContext>;
};

const useActivityContext = (): ActivityContextValue => {
	const value = useContext(ActivityContext);
	if (!value) throw new Error("Activity hooks must be used inside <ActivityProvider>");
	return value;
};

export const useActivityTracker = (): ActivityTracker => useActivityContext().tracker;

/** The tracker when inside `<ActivityProvider>`, else null (session hooks run outside the shell too). */
export const useOptionalActivityTracker = (): ActivityTracker | null => useContext(ActivityContext)?.tracker ?? null;

/** The scope ref, read by the advisor so a chat turn knows what the user is looking at. */
export const useActivityScopeRef = () => useActivityContext().scope;

/**
 * Declares the Company › Project › Solution the current page is about. Every event tracked while
 * the page is mounted carries it, and the context service keeps it as the user's focus.
 */
export const useActivityScope = (scope: ActivityScope | undefined) => {
	const ref = useActivityContext().scope;
	const { companyId, projectId, solutionId } = scope ?? {};
	useEffect(() => {
		if (!companyId && !projectId && !solutionId) return;
		const value = { companyId, projectId, solutionId };
		ref.current = value;
		return () => {
			if (ref.current === value) ref.current = undefined;
		};
	}, [companyId, projectId, solutionId, ref]);
};

/** Tab session id, shared with chat turns so the context service can line them up with activity. */
export const getActivitySessionId = tabSessionId;
